'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { fetchCachedApi, CLIENT_CACHE_KEYS, CLIENT_CACHE_EVENTS, notifyCreditsUpdated } from '@/lib/client-request-cache';
import { dailyCreditNoticeKey, shouldShowDailyCreditNotice, nextDailyCreditReset } from '@/lib/daily-credit-window';
import DailyCreditRestoreDialog from '@/components/DailyCreditRestoreDialog';

export default function DailyCreditRestore() {
  const supabase = useMemo(() => createClient(), []);
  const [userId, setUserId] = useState(null);
  const [credits, setCredits] = useState(null);
  const [acknowledgedPeriod, setAcknowledgedPeriod] = useState(null);
  const [blocked, setBlocked] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(false);
  const requestVersion = useRef(0);
  const restoreInFlight = useRef(false);
  const activeUser = useRef(userId);
  const lastAllowance = useRef(null);
  activeUser.current = userId;

  useEffect(() => {
    let active = true;
    // Only resolves client identity; /api/credits independently authenticates every request.
    supabase.auth.getSession().then(({ data }) => {
      if (active) setUserId(data.session?.user?.id || null);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  const refreshCredits = useCallback(async (force = false) => {
    if (!userId) return null;
    const version = requestVersion.current;
    let result = await fetchCachedApi('/api/credits', { cacheKey: CLIENT_CACHE_KEYS.credits, ttlMs: 30000, force });
    // An account switch can overlap a shared request started by Header for the old account.
    if (version === requestVersion.current && result.ok && result.data?.userId !== userId) {
      result = await fetchCachedApi('/api/credits', { cacheKey: CLIENT_CACHE_KEYS.credits, ttlMs: 30000, force: true });
    }
    if (version !== requestVersion.current || !result.ok || result.data?.userId !== userId) return null;
    setCredits(result.data);
    const previous = lastAllowance.current;
    lastAllowance.current = { userId, periodUtc: result.data.dailyCreditWindow?.periodUtc };
    if (previous?.userId === userId && previous.periodUtc !== lastAllowance.current.periodUtc) notifyCreditsUpdated();
    return result.data;
  }, [userId]);
  const cancelPending = useCallback(() => { ++requestVersion.current; }, []);

  useEffect(() => {
    setCredits(null);
    setError(false);
    setRestoring(false);
    restoreInFlight.current = false;
    let acknowledged = null;
    if (userId) {
      try { acknowledged = localStorage.getItem(dailyCreditNoticeKey(userId)); } catch { /* Memory state still prevents repeat notices. */ }
    }
    setAcknowledgedPeriod(acknowledged);
    refreshCredits(true).catch(() => {});
    const onCreditsUpdated = () => { refreshCredits(true).catch(() => {}); };
    const onStorage = event => {
      if (userId && event.key === dailyCreditNoticeKey(userId)) setAcknowledgedPeriod(event.newValue);
    };
    window.addEventListener(CLIENT_CACHE_EVENTS.creditsUpdated, onCreditsUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      cancelPending();
      window.removeEventListener(CLIENT_CACHE_EVENTS.creditsUpdated, onCreditsUpdated);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId, refreshCredits, cancelPending]);

  useEffect(() => {
    if (!userId) return;
    // No polling: check the new UTC allowance on foreground use or the UTC boundary.
    const onForeground = () => {
      if (document.visibilityState === 'visible') refreshCredits(true).catch(() => {});
    };
    const timer = window.setTimeout(onForeground, new Date(nextDailyCreditReset()).getTime() - Date.now() + 1000);
    window.addEventListener('focus', onForeground);
    document.addEventListener('visibilitychange', onForeground);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', onForeground);
      document.removeEventListener('visibilitychange', onForeground);
    };
  }, [userId, refreshCredits, credits?.dailyCreditWindow?.periodUtc]);

  useEffect(() => {
    // Queue this notice behind existing dialogs, including legacy custom modals.
    const check = () => {
      const anotherDialog = Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"], [data-botgo-modal]'))
        .some(element => !element.hasAttribute('data-daily-credit-restore') && element.getAttribute('data-state') !== 'closed');
      setBlocked(anotherDialog);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, []);

  const acknowledge = period => {
    if (!userId || !period || activeUser.current !== userId) return;
    setAcknowledgedPeriod(period);
    setError(false);
    try { localStorage.setItem(dailyCreditNoticeKey(userId), period); } catch { /* Storage is optional. */ }
  };

  const restore = async () => {
    if (restoreInFlight.current) return;
    restoreInFlight.current = true;
    setRestoring(true);
    setError(false);
    try {
      // Uses the existing automatic, locked, once-per-UTC-day reset. Never adds a separate bonus.
      const refreshed = await refreshCredits(true);
      if (activeUser.current !== userId) return;
      if (!refreshed) throw new Error('Unable to confirm daily credits');
      acknowledge(refreshed.dailyCreditWindow.periodUtc);
      notifyCreditsUpdated();
    } catch {
      if (activeUser.current === userId) setError(true);
    } finally {
      if (activeUser.current === userId) {
        restoreInFlight.current = false;
        setRestoring(false);
      }
    }
  };

  const open = credits?.userId === userId && !blocked && shouldShowDailyCreditNotice(credits, acknowledgedPeriod);
  return (
    <DailyCreditRestoreDialog
      open={Boolean(open)}
      credits={credits}
      restoring={restoring}
      error={error}
      onRestore={restore}
      onDismiss={() => acknowledge(credits?.dailyCreditWindow?.periodUtc)}
    />
  );
}
